import Constants from 'expo-constants';
import { Platform } from 'react-native';
import { router } from 'expo-router';
import { env } from '../config/environment';
const { safeLog } = require('../utils/safeLog.js');

export interface NotificationPermissionState {
  hasNotificationPermission: boolean;
  pushDisabled: boolean;
  subscribed: boolean;
}

// Centralized notification save queue to prevent duplicates
class NotificationSaveQueue {
  private static instance: NotificationSaveQueue;
  private savePromises = new Map<string, Promise<boolean>>();
  private processingNotifications = new Set<string>();

  static getInstance(): NotificationSaveQueue {
    if (!NotificationSaveQueue.instance) {
      NotificationSaveQueue.instance = new NotificationSaveQueue();
    }
    return NotificationSaveQueue.instance;
  }

  async saveNotification(notificationId: string, saveFunction: () => Promise<boolean>): Promise<boolean> {
    // If already processing this notification, wait for the existing promise
    if (this.savePromises.has(notificationId)) {
      safeLog.info('🔒 Notification already being saved, waiting for existing promise:', notificationId);
      return await this.savePromises.get(notificationId)!;
    }

    // If already processed, skip
    if (this.processingNotifications.has(notificationId)) {
      safeLog.info('✅ Notification already processed:', notificationId);
      return true;
    }

    // Create and store the save promise
    const savePromise = this.executeSave(notificationId, saveFunction);
    this.savePromises.set(notificationId, savePromise);

    try {
      const result = await savePromise;

      // Mark as processed if successful
      if (result) {
        this.processingNotifications.add(notificationId);
      }

      return result;
    } finally {
      // Always clean up the promise
      this.savePromises.delete(notificationId);
    }
  }

  private async executeSave(notificationId: string, saveFunction: () => Promise<boolean>): Promise<boolean> {
    try {
      safeLog.info('💾 Executing save for notification:', notificationId);
      return await saveFunction();
    } catch (error) {
      safeLog.error('❌ Error in notification save execution:', error);
      return false;
    }
  }

  isProcessed(notificationId: string): boolean {
    return this.processingNotifications.has(notificationId);
  }

  // Clean up old processed notifications periodically
  cleanup(): void {
    if (this.processingNotifications.size > 1000) {
      safeLog.info('🧹 Cleaning up notification processing cache');
      this.processingNotifications.clear();
    }
  }
}

// FIX: Global guard that persists across module reloads 
declare global {
  var __onesignal_initialization_count: number;
  var __onesignal_is_initialized: boolean;
}

class OneSignalService {
  private static isInitialized = false;
  private static notificationContext: any = null;
  private static saveQueue = NotificationSaveQueue.getInstance();

  private initialized = false;
  private currentUserId: string | null = null;
  private initializationPromise: Promise<void> | null = null;
  private permissionRequestInProgress = false;
  private notificationContext: any = null;
  private OneSignalModule: any = null;
  private isWebEnvironment = false;
  private lastImportError: string | null = null;
  private lastInitError: string | null = null;
  // DIAGNOSTIC (June 2026 push investigation): in-memory guard so the
  // steady-state push status is reported to the server at most once per app
  // session. See reportPushStatusOnce().
  private pushStatusReported = false;
  // Persisted flag set once the OS notification prompt has actually been shown
  // (see requestPermission). The login-time report is gated on this so a
  // first-session Google signup — whose account/JWT exists BEFORE onboarding
  // asks for permission — is not reported prematurely as denied/token-missing.
  private readonly PUSH_PROMPT_SHOWN_KEY = '@padrinho:pushPromptShown';
  // RELIABILITY (June 2026): the original report path marked itself "sent"
  // BEFORE the network POST and polled tokens inline, so a backgrounded app or
  // a failed POST meant the report never arrived — the reason ZERO Android
  // reports showed up. These fields make reporting coalesced (one delayed
  // send), retryable, and marked sent ONLY after a confirmed success.
  private pushStatusReportInFlight = false;
  private pushStatusReportTimer: any = null;
  private pendingReportRequirePromptShown = false;
  // Register the pushSubscription 'change' observer exactly once (init retries / HMR).
  private subscriptionObserverRegistered = false;
  // Prevent overlapping idempotent re-link calls when a token arrives late.
  private relinkInFlight = false;
  private lastObservedToken: string | null = null;
  // Bounded post-login token verification runs at most once per user per session.
  private tokenVerifyInProgressFor: string | null = null;

  // Environment detection with improved platform detection
  getEnvironmentInfo() {
    const executionEnvironment = Constants.executionEnvironment;
    const isExpoGo = executionEnvironment === 'storeClient';
    const isWeb = Platform.OS === 'web';

    // Additional Expo Go detection methods
    const isExpoGoByAppConfig = Constants.expoConfig?.slug === 'padrinho-elo' && !Constants.expoConfig?.extra?.eas;
    const isExpoGoByManifest = Constants.appOwnership === 'expo';
    const isDefinitelyExpoGo = isExpoGo || isExpoGoByAppConfig || isExpoGoByManifest;

    const isDevelopmentClient = executionEnvironment === 'standalone' && Constants.expoConfig?.extra?.eas;
    const isStandaloneBuild = executionEnvironment === 'standalone';
    const isBareWorkflow = executionEnvironment === 'bare';
    const isEASBuild = isDevelopmentClient || isStandaloneBuild || isBareWorkflow;
    
    // Precise preview build detection - only target actual preview environments
    const isPreviewBuild = (
      Constants.appOwnership === 'expo' || 
      Constants.executionEnvironment === 'storeClient'
    );

    // Check if OneSignal is globally disabled via kill switch
    const isOneSignalDisabled = process.env.EXPO_PUBLIC_ONESIGNAL_ENABLED === 'false';
    
    // Check if OneSignal App ID is available. Use the RESOLVED config value
    // (config/environment.ts), NOT process.env directly: in production the
    // EXPO_PUBLIC_ONESIGNAL_APP_ID placeholder shipped unresolved, so reading
    // process.env here returned the literal "${EXPO_PUBLIC_ONESIGNAL_APP_ID}"
    // (truthy garbage) → SDK initialized against an invalid app and no device
    // ever got a push subscription. env.oneSignalAppId is always a real App ID.
    // Note: Only APP_ID should be on client - API_KEY must stay server-side only!
    const hasOneSignalEnvVars = !!(
      env.oneSignalAppId && env.oneSignalAppId.trim() !== ''
    );

    // OneSignal React Native SDK only works on native platforms (iOS/Android), not web
    // Must exclude preview builds to prevent crashes and respect kill switch
    const isSupported = !isOneSignalDisabled && !isWeb && !isPreviewBuild && (Platform.OS === 'ios' || Platform.OS === 'android') && hasOneSignalEnvVars;

    return {
      executionEnvironment,
      isExpoGo: isDefinitelyExpoGo,
      isDevelopmentClient,
      isStandaloneBuild,
      isBareWorkflow,
      isEASBuild,
      isPreviewBuild,
      isOneSignalDisabled,
      hasOneSignalEnvVars,
      isSupported,
      isInitialized: this.initialized,
      isWeb,
      platform: Platform.OS
    };
  }

  // Safe module loader that only works in supported native environments
  private async loadOneSignalModule(): Promise<any> {
    const envInfo = this.getEnvironmentInfo();

    // OneSignal React Native SDK does not work on web
    if (envInfo.isWeb) {
      safeLog.info('📱 Web environment detected - OneSignal React Native SDK not supported');
      throw new Error('OneSignal React Native SDK not supported on web platform');
    }

    if (envInfo.isExpoGo) {
      throw new Error('OneSignal not available in Expo Go');
    }

    if (!envInfo.isSupported) {
      throw new Error('OneSignal not supported in current environment');
    }

    // Only load the module if we haven't already and we're in a supported environment
    if (!this.OneSignalModule) {
      try {
        // Attempt to import OneSignal with enhanced error handling
        let oneSignalModule;
        try {
          // Add additional safety check before import
          if (typeof require === 'undefined') {
            throw new Error('require() not available - incompatible environment');
          }
          
          oneSignalModule = await import('react-native-onesignal');
        } catch (importError: any) {
          safeLog.error('❌ OneSignal module import failed:', importError?.message || 'Unknown import error');
          
          // Handle module not found gracefully - this is expected in development/preview builds
          if (importError?.message?.includes('Unable to resolve module') ||
              importError?.message?.includes('Module not found') ||
              importError?.message?.includes('Cannot resolve module') ||
              importError?.message?.includes('TurboModuleRegistry') ||
              importError?.message?.includes('NativeModules') ||
              importError?.code === 'MODULE_NOT_FOUND') {
            throw new Error('OneSignal native module not available - expected in development/preview builds without EAS');
          }
          
          throw importError;
        }

        safeLog.info('🔍 OneSignal module loaded:', {
          hasDefault: !!oneSignalModule.default,
          hasOneSignal: !!oneSignalModule.OneSignal,
          moduleKeys: Object.keys(oneSignalModule),
        });

        // For react-native-onesignal v5+, the main export is OneSignal directly
        let candidateModule = null;

        if (oneSignalModule.OneSignal && typeof oneSignalModule.OneSignal === 'object') {
          candidateModule = oneSignalModule.OneSignal;
          safeLog.info('✅ Found OneSignal export in module');
        } else if (oneSignalModule.default && typeof oneSignalModule.default === 'object') {
          candidateModule = oneSignalModule.default;
          safeLog.info('✅ Found default export in module');
        }

        if (!candidateModule) {
          safeLog.error('❌ OneSignal module structure unexpected:', Object.keys(oneSignalModule));
          throw new Error('OneSignal module not found in expected structure');
        }

        this.OneSignalModule = candidateModule;

        // Validate that we have the essential methods according to OneSignal v5+ SDK
        const requiredMethods = ['initialize', 'Notifications', 'User'];
        const missingMethods = requiredMethods.filter(method =>
          typeof this.OneSignalModule[method] === 'undefined'
        );

        if (missingMethods.length > 0) {
          safeLog.error('❌ OneSignal module missing required methods:', missingMethods);
          safeLog.info('📋 Available methods:', Object.keys(this.OneSignalModule));

          // Check if methods are available under different patterns
          const availableMethods = Object.keys(this.OneSignalModule);
          if (availableMethods.includes('initialize') || availableMethods.includes('setAppId')) {
            safeLog.info('✅ OneSignal module found with alternative method names');
          } else {
            throw new Error(`OneSignal module missing essential methods: ${missingMethods.join(', ')}`);
          }
        }

        this.lastImportError = null;
        safeLog.info('✅ OneSignal module loaded successfully');

      } catch (error) {
        this.lastImportError = (error as any)?.message || String(error);
        safeLog.error('❌ Failed to load OneSignal module:', error.message);
        throw error;
      }
    }

    return this.OneSignalModule;
  }

  async initialize(): Promise<void> {
    // FIX: Global initialization guard that persists across module reloads
    if (!globalThis.__onesignal_initialization_count) {
      globalThis.__onesignal_initialization_count = 0;
    }
    
    if (globalThis.__onesignal_is_initialized) {
      safeLog.info('✅ OneSignal already initialized globally - skipping');
      
      // Ensure the module is loaded even if we skip initialization
      if (!this.OneSignalModule) {
        try {
          await this.loadOneSignalModule();
          this.initialized = true;
        } catch (e) {
          // FIX (step 1): Clear the stale global flag so the NEXT call runs the
          // full _initialize() path instead of hitting this early-return again
          // with a null module. Without this clear, setExternalUserId re-enters
          // initialize(), hits the globalThis guard, fails the load again, and
          // login() is never reached.
          globalThis.__onesignal_is_initialized = false;
          safeLog.warn('⚠️ Could not load OneSignal module on already-initialized instance — clearing global flag for retry');
        }
      }
      return;
    }

    globalThis.__onesignal_initialization_count++;
    safeLog.info('🔧 OneSignal initialization attempt #' + globalThis.__onesignal_initialization_count);

    // Skip initialization on web to prevent native module errors
    if (Platform.OS === 'web') {
      safeLog.info('📱 OneSignal: Web environment detected - implementing web fallback');
      this.initialized = true;
      this.isWebEnvironment = true;
      globalThis.__onesignal_is_initialized = true;
      return;
    }

    if (this.initialized || this.initializationPromise) {
      safeLog.info('📱 OneSignal: Already initialized or initialization in progress');
      await this.initializationPromise;
      return;
    }

    // FIX (step 2): Assign initializationPromise SYNCHRONOUSLY (before any await)
    // so that a concurrent call arriving on the next microtask always sees a
    // non-null promise and waits on the same work instead of launching a second
    // _initialize(). Without this latch, two callers that both pass the guard
    // above in the same microtask batch each call _initialize() independently,
    // which can invoke OneSignal.initialize() twice in quick succession.
    let resolveLatch!: () => void;
    let rejectLatch!: (err: any) => void;
    this.initializationPromise = new Promise<void>((res, rej) => {
      resolveLatch = res;
      rejectLatch = rej;
    });

    this._initialize()
      .then(() => {
        resolveLatch();
      })
      .catch((error) => {
        safeLog.error('❌ OneSignal: Critical initialization error:', error?.message || 'Unknown error');
        this.initialized = false;
        this.initializationPromise = null;
        rejectLatch(error);
      });

    return this.initializationPromise;
  }

  private async _initialize(): Promise<void> {
    let initAttempts = 0;
    const maxInitAttempts = 3;

    while (initAttempts < maxInitAttempts) {
      try {
        initAttempts++;
        safeLog.info(`🔧 OneSignal initialization attempt ${initAttempts}/${maxInitAttempts}`);

        const envInfo = this.getEnvironmentInfo();
        safeLog.info('🔧 OneSignal Environment Info:', envInfo);

        if (!envInfo.isSupported) {
          safeLog.info('⚠️ OneSignal not supported in current environment:', envInfo.executionEnvironment);
          return;
        }

        if (!env.oneSignalAppId || env.oneSignalAppId.trim() === '') {
          safeLog.warn('⚠️ OneSignal: App ID not configured - skipping initialization');
          this.initialized = false;
          return;
        }

        safeLog.info('🔧 OneSignal: Initializing with App ID:', env.oneSignalAppId.substring(0, 8) + '...');

        // Load OneSignal module with retry logic
        const OneSignal = await this.loadOneSignalModuleWithRetry();

        // Initialize OneSignal with connection resilience
        try {
          safeLog.info('🔄 Calling OneSignal.initialize...');
          
          // Add try-catch around the actual initialization call with async safety
          try {
            // Wrap in a promise to handle potential synchronous native crashes
            await new Promise((resolve, reject) => {
              try {
                // Add a timeout to prevent hanging
                const timeoutId = setTimeout(() => {
                  reject(new Error('OneSignal initialization timeout'));
                }, 10000);
                
                // Call OneSignal.initialize safely
                OneSignal.initialize(env.oneSignalAppId);
                
                // FIX (step 3): Reduced from 1000ms → 100ms.
                // Two consecutive 1-second delays were adding ≥2s before login()
                // could proceed. The SIGNED_IN event fires ~300ms after signup,
                // so login() was always racing against this artificial gate.
                setTimeout(() => {
                  clearTimeout(timeoutId);
                  resolve(true);
                }, 100);
                
              } catch (syncError) {
                reject(syncError);
              }
            });
          } catch (initCallError) {
            safeLog.error('❌ OneSignal.initialize() call failed:', initCallError);
            throw new Error(`OneSignal initialization call failed: ${initCallError.message}`);
          }

          // FIX (step 3): Reduced from 1000ms → 100ms (second delay).
          await new Promise(resolve => setTimeout(resolve, 100));

          safeLog.info('✅ OneSignal: Initialized successfully');

          // Set up event listeners
          this.setupOneSignalEventListeners(OneSignal);

          // NOTE: Permission is NOT requested here.
          // initialize() is silent — it only registers the device with OneSignal.
          // The permission prompt is shown separately from onboarding-notifications.tsx
          // via requestPermission(), which keeps user consent in the correct UX context.

          this.initialized = true;
          this.lastInitError = null;
          globalThis.__onesignal_is_initialized = true; // FIX: Mark globally initialized
          safeLog.info('🎉 OneSignal: Service initialized successfully');

          return; // Success - exit retry loop

        } catch (initError) {
          safeLog.error(`❌ OneSignal initialization attempt ${initAttempts} failed:`, initError);

          if (initAttempts >= maxInitAttempts) {
            throw initError;
          }

          // Wait before retry with exponential backoff
          const delay = 2000 * Math.pow(2, initAttempts - 1);
          safeLog.info(`⏳ Waiting ${delay}ms before retry...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }

      } catch (error) {
        this.lastInitError = (error as any)?.message || String(error);
        safeLog.error(`❌ OneSignal attempt ${initAttempts} failed:`, error);

        if (initAttempts >= maxInitAttempts) {
          safeLog.error('❌ OneSignal: All initialization attempts failed');
          this.initialized = false;
          throw error;
        }

        // Wait before retry
        const delay = 1000 * initAttempts;
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  private async loadOneSignalModuleWithRetry(): Promise<any> {
    if (this.OneSignalModule) {
      return this.OneSignalModule;
    }

    let attempts = 0;
    const maxAttempts = 3;

    while (attempts < maxAttempts) {
      try {
        attempts++;
        safeLog.info(`🔄 Loading OneSignal module attempt ${attempts}/${maxAttempts}`);

        return await this.loadOneSignalModule();
      } catch (error) {
        safeLog.warn(`⚠️ Module load attempt ${attempts} failed:`, error);

        if (attempts >= maxAttempts) {
          throw error;
        }

        await new Promise(resolve => setTimeout(resolve, 1000 * attempts));
      }
    }
  }

  private setupOneSignalEventListeners(OneSignal: any): void {
    try {
      // Set up notification event listeners
      if (OneSignal && OneSignal.Notifications) {
        try {
          OneSignal.Notifications.addEventListener('click', (event: any) => {
            safeLog.info('🔔 OneSignal notification clicked:', event);
            this.handleNotificationClick(event);
          });
          safeLog.info('✅ OneSignal click listener registered');
        } catch (clickError) {
          safeLog.warn('⚠️ Failed to register click listener:', clickError?.message || 'Unknown error');
        }

        try {
          OneSignal.Notifications.addEventListener('foregroundWillDisplay', (event: any) => {
            safeLog.info('🔔 OneSignal notification received in foreground:', event);
            this.handleNotificationReceived(event);

            // Allow the notification to be displayed
            if (event && event.getNotification) {
              event.getNotification().display();
            }
          });
          safeLog.info('✅ OneSignal foreground listener registered');
        } catch (foregroundError) {
          safeLog.warn('⚠️ Failed to register foreground listener:', foregroundError?.message || 'Unknown error');
        }

        try {
          OneSignal.Notifications.addEventListener('permissionChange', (granted: boolean) => {
            safeLog.info('🔔 OneSignal permission changed:', granted);
          });
          safeLog.info('✅ OneSignal permission listener registered');
        } catch (permissionError) {
          safeLog.warn('⚠️ Failed to register permission listener:', permissionError?.message || 'Unknown error');
        }
      } else {
        safeLog.warn('⚠️ OneSignal.Notifications not available');
      }

      // LATE-TOKEN OBSERVER (June 2026): fires whenever the device push
      // subscription changes (token / id / optedIn). This is the primitive that
      // lets a late-arriving FCM/APNs token (slow device / short session) still
      // (a) re-link the authenticated user and (b) produce ONE accurate
      // diagnostic report — without it an email signup whose token lands after
      // login() stays subscribed with an empty token.
      try {
        const subApi = OneSignal?.User?.pushSubscription;
        if (subApi?.addEventListener && !this.subscriptionObserverRegistered) {
          subApi.addEventListener('change', (event: any) => {
            this.handlePushSubscriptionChange(event);
          });
          this.subscriptionObserverRegistered = true;
          safeLog.info('✅ OneSignal pushSubscription change listener registered');
        }
      } catch (subError: any) {
        safeLog.warn('⚠️ Failed to register pushSubscription listener:', subError?.message || 'Unknown error');
      }

      safeLog.info('✅ OneSignal event listeners setup completed');
    } catch (error) {
      safeLog.warn('⚠️ Could not set up OneSignal listeners:', {
        message: error?.message || 'Unknown error',
        code: error?.code || 'No code',
        stack: error?.stack || 'No stack trace',
        isWeb: Platform.OS === 'web',
        hasOneSignal: typeof OneSignal !== 'undefined',
        hasNotifications: !!(OneSignal && OneSignal.Notifications)
      });
    }
  }

  private handleNotificationReceived(event: any): void {
    try {
      const notification = event.getNotification();
      safeLog.info('📨 Processing received notification - Full Event:', JSON.stringify(event, null, 2));
      safeLog.info('📨 Processing received notification - Notification Object:', JSON.stringify(notification, null, 2));
      safeLog.info('📨 Processing received notification - Key fields:', {
        notificationId: notification.notificationId,
        title: notification.title,
        body: notification.body,
        subtitle: notification.subtitle,
        additionalData: notification.additionalData,
        bigPicture: notification.bigPicture,
        launchURL: notification.launchURL,
        icon: notification.icon,
        backgroundColor: notification.backgroundColor
      });

      // Validate required fields before attempting to save
      if (!notification.notificationId) {
        safeLog.error('❌ Missing notificationId, cannot save notification');
        return;
      }

      if (!notification.title && !notification.body) {
        safeLog.error('❌ Missing both title and body, cannot save notification');
        return;
      }

      // Use centralized save queue with improved deduplication
      OneSignalService.saveQueue.saveNotification(notification.notificationId, async () => {
        return await OneSignalService.saveNotificationToDatabase(notification, notification);
      }).then(success => {
        if (success) {
          safeLog.info('✅ Notification saved successfully via queue');
        } else {
          safeLog.warn('⚠️ Failed to save notification via queue');
        }
      }).catch(error => {
        safeLog.error('❌ Error in notification save queue:', error);
      });

    } catch (error) {
      safeLog.error('❌ Failed to handle received notification:', error);
      safeLog.error('❌ Error details:', {
        message: error?.message,
        stack: error?.stack,
        event: event
      });
    }
  }

  private handleNotificationClick(event: any): void {
    try {
      // OneSignal click event structure varies - handle both formats
      // Some SDK versions: event.getNotification(), others: event.notification or event itself
      let notification;
      if (typeof event?.getNotification === 'function') {
        notification = event.getNotification();
      } else if (event?.notification) {
        notification = event.notification;
      } else {
        // Event itself might be the notification object
        notification = event;
      }
      
      safeLog.info('👆 Processing clicked notification:', notification);

      const additionalData = notification?.additionalData || notification?.data || {};
      const notificationId = notification?.notificationId || notification?.notification_id;

      // PRD §5.11 — `notification_opened`. Captures all push-notification
      // taps (banners, lock screen, app-icon badge). `category` comes
      // from the OneSignal `additionalData.type` field, falling back to
      // `'unknown'` so the event always ships with a non-empty bucket.
      import('@/utils/clarityEvents')
        .then(({ logNotificationOpened }) =>
          logNotificationOpened({
            category: additionalData?.type ? String(additionalData.type) : 'unknown',
          })
        )
        .catch(() => {});

      // Mark notification as read in database
      this.markNotificationAsRead(notificationId).then(success => {
        if (success) {
          safeLog.info('✅ Notification marked as read on click');
        }
      }).catch(error => {
        safeLog.warn('⚠️ Failed to mark notification as read:', error?.message);
      });

      // Decrement badge count when user taps a push notification
      this.decrementBadgeOnTap();
      
      // Check if this is a meeting reminder notification
      if (additionalData.type === 'meeting_reminder') {
        safeLog.info('📅 Handling meeting reminder notification click');
        this.handleMeetingReminderAction(additionalData);
      } else if (additionalData.type === 'appointment_reminder') {
        // Sponsor↔patient call T-30 reminder. Push payload carries
        // `appointment_id` (spread from variables) and also `source_id`
        // as a belt-and-suspenders fallback. The shared helper mints a
         // fresh signed join token, runs the permissions pre-check, and
        // routes to /video-call/lobby (or /permissions). Lazy-imported
        // so cold-start push taps don't pull the whole sponsor service
        // into the OneSignal init path.
        safeLog.info('📅 Handling appointment reminder notification click');
        const appointmentId = additionalData.appointment_id || additionalData.source_id;
        import('./joinAppointmentFromNotification')
          .then(({ joinAppointmentFromNotification }) => joinAppointmentFromNotification(appointmentId))
          .catch(err => safeLog.error('❌ Failed to dispatch appointment join from push:', err));
      } else if (additionalData.link || additionalData.url) {
        this.handleNotificationAction(additionalData.link || additionalData.url);
      }
    } catch (error) {
      safeLog.error('❌ Failed to handle notification click:', error);
    }
  }

  /**
   * Decrement badge count when user taps a notification
   */
  private async decrementBadgeOnTap(): Promise<void> {
    try {
      const { default: BadgeManagerService } = await import('./badgeManagerService');
      await BadgeManagerService.decrementBadge();
      safeLog.info('📱 Badge decremented after tapping push notification');
    } catch (error) {
      safeLog.warn('⚠️ Failed to decrement badge:', error?.message);
    }
  }

  /**
   * Mark a notification as read by OneSignal notification ID
   */
  private async markNotificationAsRead(oneSignalNotificationId: string): Promise<boolean> {
    if (!oneSignalNotificationId) {
      safeLog.warn('⚠️ No notification ID provided for marking as read');
      return false;
    }

    try {
      const { supabase } = await import('../lib/supabase');
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        safeLog.warn('⚠️ No authenticated user for marking notification as read');
        return false;
      }

      // Find the notification by OneSignal ID
      const { data: notification, error: findError } = await supabase
        .from('notifications')
        .select('id')
        .eq('onesignal_notification_id', oneSignalNotificationId)
        .single();

      if (findError || !notification) {
        safeLog.warn('⚠️ Notification not found in database:', oneSignalNotificationId);
        return false;
      }

      // Update user_notification_status to mark as read
      const { error: updateError } = await supabase
        .from('user_notification_status')
        .upsert({
          user_id: user.id,
          notification_id: notification.id,
          is_read: true,
          read_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,notification_id'
        });

      if (updateError) {
        safeLog.error('❌ Failed to update notification status:', updateError.message);
        return false;
      }

      safeLog.info('✅ Notification marked as read:', notification.id);

      // Update cache
      try {
        const { default: NotificationCacheService } = await import('./notificationCacheService');
        await NotificationCacheService.markAsReadInCache(user.id, notification.id);
      } catch (cacheError: any) {
        safeLog.warn('⚠️ Failed to update cache:', cacheError?.message);
      }

      // Sync badge count
      try {
        const { BadgeService } = await import('./badgeService');
        await BadgeService.syncBadgeWithNotifications();
        safeLog.info('🏷️ Badge synced after marking as read');
      } catch (badgeError: any) {
        safeLog.warn('⚠️ Failed to sync badge:', badgeError?.message);
      }

      // Trigger NotificationContext refresh via global context
      try {
        const { triggerGlobalNotificationRefresh } = await import('../contexts/NotificationContext');
        await triggerGlobalNotificationRefresh();
        safeLog.info('🔄 NotificationContext refreshed after marking as read via global');
      } catch (refreshError: any) {
        safeLog.warn('⚠️ Failed to refresh NotificationContext:', refreshError?.message);
      }

      return true;
    } catch (error: any) {
      safeLog.error('❌ Exception marking notification as read:', error?.message);
      return false;
    }
  }

  private handleNotificationAction(action: string): void {
    try {
      safeLog.info('🔗 Handling notification action:', action);

      // Handle different action types
      if (action.startsWith('http')) {
        // External URL - could open in browser
        safeLog.info('🔗 External URL action:', action);
        this.openExternalUrl(action);
      } else {
        // Internal navigation action
        safeLog.info('📱 Internal navigation action:', action);
      }
    } catch (error) {
      safeLog.error('❌ Failed to handle notification action:', error);
    }
  }

  /**
   * Handle meeting reminder notification clicks
   * Navigates to meeting detail page where user can see info and choose to join
   */
  private handleMeetingReminderAction(data: any): void {
    try {
      safeLog.info('📅 Processing meeting reminder click:', {
        meetingName: data.meeting_name,
        meetingId: data.meeting_id
      });

      // Priority: Navigate to meeting detail page if we have the meeting ID
      if (data.meeting_id) {
        safeLog.info('📱 Navigating to meeting detail page:', data.meeting_id);
        router.push(`/meeting/${data.meeting_id}`);
        return;
      }

      // Fallback: Navigate to meetings section if no meeting ID
      safeLog.info('📱 No meeting ID, navigating to meetings section');
      router.push('/(tabs)/encontros-online');
      
    } catch (error) {
      safeLog.error('❌ Error handling meeting reminder action:', error);
    }
  }

  /**
   * Open external URL (meeting links, etc.)
   */
  private async openExternalUrl(url: string): Promise<void> {
    try {
      safeLog.info('🌐 OneSignal: Opening external URL:', url);

      // Clean and validate the URL first (same logic as meeting cards)
      let cleanUrl = url.trim();
      
      // Remove any potential file:// prefix if it exists
      if (cleanUrl.startsWith('file://')) {
        const urlPart = cleanUrl.split('/').pop() || '';
        cleanUrl = urlPart;
      }
      
      // Ensure URL has protocol - add https:// if missing
      let finalUrl = cleanUrl;
      if (!finalUrl.startsWith('http://') && !finalUrl.startsWith('https://')) {
        finalUrl = `https://${finalUrl}`;
      }

      safeLog.info('🔗 OneSignal: URL processing:', {
        original: url,
        cleaned: cleanUrl,
        final: finalUrl
      });

      // For React Native environments, try direct Linking first (skip WebBrowser to avoid file:// issues)
      if (typeof window === 'undefined') {
        try {
          // Use React Native Linking directly - skip canOpenURL check to avoid file:// conversion
          const { Linking } = await import('react-native');
          safeLog.info('🔗 OneSignal: Opening with Linking.openURL:', finalUrl);
          await Linking.openURL(finalUrl);
          safeLog.info('✅ OneSignal: Opened URL with Linking API');
          return;
        } catch (linkingError) {
          safeLog.error('❌ OneSignal: Linking API failed:', linkingError);
          
          // Fallback to WebBrowser if Linking fails
          try {
            const { WebBrowser } = await import('expo-web-browser');
            await WebBrowser.openBrowserAsync(finalUrl, {
              dismissButtonStyle: 'close',
              presentationStyle: 'pageSheet',
              controlsColor: '#151515',
              toolbarColor: '#F9F8F3'
            });
            safeLog.info('✅ OneSignal: Opened URL with WebBrowser fallback');
            return;
          } catch (webBrowserError) {
            safeLog.error('❌ OneSignal: WebBrowser fallback failed:', webBrowserError);
          }
        }
      } else {
        // For web environments
        window.open(finalUrl, '_blank', 'noopener,noreferrer');
        safeLog.info('✅ OneSignal: Opened URL in new tab');
      }
    } catch (error) {
      safeLog.error('❌ OneSignal: Failed to open external URL:', error);
    }
  }

  /**
   * Centralized notification save method - SINGLE SOURCE OF TRUTH
   * Saves notification to database, creates user_notification_status, updates badge
   */
  private static async saveNotificationToDatabase(notificationData: any, rawNotification: any): Promise<boolean> {
    const startTime = Date.now();
    safeLog.info('💾 [Centralized] Starting notification save process...');
    safeLog.info('🔍 [Debug] Raw notification data received:', JSON.stringify(notificationData, null, 2));
    safeLog.info('🔍 [Debug] Raw notification additional data:', JSON.stringify(rawNotification, null, 2));

    try {
      // Enhanced data cleaning and validation - Fixed message mapping
      const cleanNotificationData = {
        onesignal_notification_id: notificationData.notificationId || `client-${Date.now()}`,
        title: notificationData.title || rawNotification?.title || 'Notification',
        subtitle: notificationData.subtitle || rawNotification?.subtitle || notificationData.additionalData?.subtitle,
        message: notificationData.body || rawNotification?.body || notificationData.message || '',
        banner_url: notificationData.bigPicture || rawNotification?.bigPicture || notificationData.additionalData?.banner_url,
        link_url: notificationData.launchURL || rawNotification?.launchURL || notificationData.additionalData?.link || notificationData.additionalData?.link_url,
        icon: notificationData.icon || notificationData.additionalData?.icon || 'bell',
        background_color: notificationData.backgroundColor || notificationData.additionalData?.backgroundColor || notificationData.additionalData?.background_color || '#F0EEE4',
        target_type: 'all' as const,
        is_active: true,
        sent_at: new Date().toISOString()
      };

      safeLog.info('📊 [Centralized] Prepared notification data for database:', {
        title: cleanNotificationData.title,
        message: cleanNotificationData.message,
        subtitle: cleanNotificationData.subtitle,
        oneSignalId: cleanNotificationData.onesignal_notification_id,
        fullData: cleanNotificationData
      });

      const { NotificationsService } = await import('./notificationsService');
      const result = await NotificationsService.createNotification(cleanNotificationData);

      if (result.success) {
        const saveTimeMs = Date.now() - startTime;
        safeLog.info('✅ [Centralized] Notification saved successfully:', {
          id: result.data.id,
          title: cleanNotificationData.title,
          message: cleanNotificationData.message,
          oneSignalId: cleanNotificationData.onesignal_notification_id,
          saveTimeMs
        });

        // Get current user and create user_notification_status record via API
        try {
          const { supabase } = await import('../lib/supabase');
          const { data: { user } } = await supabase.auth.getUser();
          
          if (user) {
            // Create user_notification_status record via API (bypasses RLS)
            const statusResult = await NotificationsService.createUserNotificationStatusViaAPI(
              result.data.id,
              user.id,
              false
            );

            if (!statusResult.success) {
              safeLog.warn('⚠️ Failed to create user_notification_status:', statusResult.error);
            } else {
              safeLog.info('✅ [Centralized] User notification status created (unread) via API');
            }

            // Add to cache for immediate UI update
            const { default: NotificationCacheService } = await import('./notificationCacheService');
            const userNotificationStatus = {
              id: '',
              notification_id: result.data.id,
              is_read: false,
              read_at: null,
              notification: {
                id: result.data.id,
                title: cleanNotificationData.title,
                subtitle: cleanNotificationData.subtitle,
                message: cleanNotificationData.message,
                banner_url: cleanNotificationData.banner_url,
                link_url: cleanNotificationData.link_url,
                icon: cleanNotificationData.icon,
                background_color: cleanNotificationData.background_color,
                sent_at: cleanNotificationData.sent_at
              }
            };
            await NotificationCacheService.addNotificationToCache(user.id, userNotificationStatus);
            safeLog.info('📦 [Centralized] Notification added to cache for immediate UI update');

            // Update badge count
            try {
              const { BadgeService } = await import('./badgeService');
              await BadgeService.syncBadgeWithNotifications();
              safeLog.info('🏷️ [Centralized] Badge synced after notification save');
            } catch (badgeError: any) {
              safeLog.warn('⚠️ Failed to sync badge:', badgeError?.message);
            }

            // Trigger NotificationContext refresh via global context
            try {
              const { triggerGlobalNotificationRefresh } = await import('../contexts/NotificationContext');
              await triggerGlobalNotificationRefresh();
              safeLog.info('🔄 [Centralized] NotificationContext refreshed via global');
            } catch (refreshError: any) {
              safeLog.warn('⚠️ Failed to refresh NotificationContext:', refreshError?.message);
            }
          }
        } catch (userError: any) {
          safeLog.warn('⚠️ Failed to create user notification status:', userError?.message);
        }

        return true;
      } else {
        safeLog.error('❌ [Centralized] Failed to save notification:', result.error);
        return false;
      }
    } catch (error) {
      safeLog.error('❌ [Centralized] Exception saving notification:', error);
      return false;
    }
  }

  async isReady(): Promise<boolean> {
    const envInfo = this.getEnvironmentInfo();
    if (envInfo.isWeb || envInfo.isExpoGo || !envInfo.isSupported) {
      return false;
    }

    return this.initialized && this.OneSignalModule !== null;
  }

  // Re-links the user with OneSignal if push permission is currently granted.
  // Designed to be called on every foreground return — safe and fast because
  // it does a synchronous permission check first and only calls login() when
  // the permission is actually on. Handles the case where the user went to
  // Settings, enabled notifications, and returned to the app mid-session.
  async checkAndRelinkIfGranted(userId: string): Promise<void> {
    try {
      if (!this.initialized || !this.OneSignalModule) return;
      const OneSignal = this.OneSignalModule;
      // Use the ASYNC permission getter — the cached Notifications.permission can
      // read false on a cold start (see memory: onesignal-single-init-path),
      // which would silently skip the foreground relink/report backstop.
      let hasPermission = false;
      try {
        hasPermission = (await OneSignal?.Notifications?.getPermissionAsync?.()) === true;
      } catch {
        hasPermission = OneSignal?.Notifications?.permission === true;
      }
      if (!hasPermission) return;
      this.currentUserId = userId;
      await OneSignal.login(userId);
      safeLog.info('[OneSignal:relink] Token re-linked on foreground return (permission=granted)');
      // RELIABILITY backstop (June 2026): a brand-new user whose signup-session
      // report never reached the server (app backgrounded right after signup, or
      // a failed POST) reports on the next foreground. Per-process
      // pushStatusReported resets each launch, so this also captures the steady
      // state for the existing user base — the reason Android produced ZERO
      // reports before. Gated + scheduled; fully fire-and-forget.
      this.schedulePushStatusReport('foreground_relink', { requirePromptShown: true, delayMs: 4000 });
    } catch {
      // non-fatal — never block the foreground transition
    }
  }

  async requestPermission(): Promise<boolean> {
    const envInfo = this.getEnvironmentInfo();

    if (envInfo.isWeb) {
      safeLog.info('📱 Web environment - permission request not available');
      return false;
    }

    if (envInfo.isExpoGo) {
      safeLog.info('📱 Expo Go detected - permission request skipped');
      return false;
    }

    if (this.permissionRequestInProgress) {
      safeLog.info('⏳ Permission request already in progress...');
      return false;
    }

    if (!envInfo.isSupported) {
      safeLog.info('⚠️ OneSignal not supported, permission request skipped');
      return false;
    }

    try {
      this.permissionRequestInProgress = true;

      // Ensure OneSignal is initialized
      if (!this.initialized) {
        safeLog.info('🔧 OneSignal not initialized, attempting to initialize...');
        await this.initialize();

        // Wait a moment for initialization to complete
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      // Double-check we have the module
      if (!this.OneSignalModule) {
        safeLog.error('❌ OneSignal module not available after initialization');
        return false;
      }

      safeLog.info('📱 Requesting notification permission...');

      // Use a more robust permission request
      const response = await this.OneSignalModule.Notifications.requestPermission(true);
      const granted = response === true || response?.granted === true;

      safeLog.info('📱 Permission response:', response);
      safeLog.info('📱 Permission granted:', granted);

      // DIAGNOSTIC (June 2026 push investigation): correlate the OS-level grant
      // with whether the device actually obtains a push token. The post-1.4.1
      // symptom is "OneSignal.login() works but every user has an empty token".
      // If we see `result=granted token=MISSING` in device logs, the fault is
      // NATIVE / dashboard (APNs key, FCM v1 credentials, provisioning) — NOT the
      // in-app permission UX — and an OTA cannot fix it. If we instead see
      // `result=denied`, the issue is the prompt/flow. Fire-and-forget so we
      // never delay onboarding navigation; the APNs/FCM token arrives a moment
      // after the grant, so we poll briefly.
      if (granted) {
        void this.logPostGrantTokenStatus().catch(() => {});
      } else {
        safeLog.info('[OneSignal:permission] result=denied token=N/A');
      }

      // DIAGNOSTIC (June 2026): the OS prompt has now been answered (granted or
      // denied). Persist that fact so the login-time report knows its reading is
      // trustworthy. Then, only if the user is already authenticated (the Google
      // path: account/JWT exists before this prompt), send the server report now
      // — this is the accurate, post-prompt reading. Email signups have no JWT
      // here yet, so they are intentionally skipped and reported later from
      // setExternalUserId() once they sign in.
      try {
        const AsyncStorage = (await import('@react-native-async-storage/async-storage')).default;
        await AsyncStorage.setItem(this.PUSH_PROMPT_SHOWN_KEY, '1');
      } catch {
        // non-critical
      }
      // Run the SAME bounded token verification as the post-login path so a
      // freshly-granted user whose token is slow still produces ONE accurate,
      // settled report — and a late token (15–30s) preempts a token-missing
      // outcome instead of being frozen by a too-early scheduled report.
      // Ungated: the prompt was just answered in this exact flow.
      const promptedUserId = this.currentUserId;
      if (promptedUserId) {
        void this.verifyTokenAfterLogin(promptedUserId, { requirePromptShown: false });
      }

      return granted;

    } catch (error) {
      safeLog.error('❌ OneSignal permission request failed:', error?.message || 'Unknown error');
      return false;
    } finally {
      this.permissionRequestInProgress = false;
    }
  }

  /**
   * DIAGNOSTIC: after a permission grant, poll briefly for the device push token
   * and log the outcome with a greppable tag. APNs/FCM registration is async and
   * usually completes within a few seconds of the grant, so we check a few times.
   * This is the decisive signal that separates a permission/UX problem from a
   * native push-credential problem (see requestPermission for the rationale).
   */
  private async logPostGrantTokenStatus(): Promise<void> {
    const sub = this.OneSignalModule?.User?.pushSubscription;
    if (!sub) {
      safeLog.warn('[OneSignal:permission] result=granted token=UNKNOWN reason=no_pushSubscription_api');
      return;
    }

    // Poll at 0.5s, 1.5s, 3s, 5s — covers typical APNs/FCM registration latency.
    const delays = [500, 1000, 1500, 2000];
    for (let i = 0; i < delays.length; i++) {
      await new Promise(resolve => setTimeout(resolve, delays[i]));
      try {
        const token = await sub?.getTokenAsync?.();
        const subId = await sub?.getIdAsync?.();
        const optedIn = await sub?.getOptedInAsync?.();
        if (token) {
          safeLog.info(
            `[OneSignal:permission] result=granted token=present ` +
            `subscriptionId=${subId ? subId.substring(0, 8) : 'null'} optedIn=${optedIn} afterMs=${delays.slice(0, i + 1).reduce((a, b) => a + b, 0)}`
          );
          return;
        }
        // Last attempt with still no token — this is the smoking gun.
        if (i === delays.length - 1) {
          safeLog.warn(
            `[OneSignal:permission] result=granted token=MISSING ` +
            `subscriptionId=${subId ? subId.substring(0, 8) : 'null'} optedIn=${optedIn} ` +
            `hint=native_apns_fcm_or_dashboard_credentials`
          );
        }
      } catch (probeErr: any) {
        if (i === delays.length - 1) {
          safeLog.warn('[OneSignal:permission] post-grant token probe failed:', probeErr?.message || 'unknown');
        }
      }
    }
  }

  /**
   * RELIABILITY (June 2026): observer for device push-subscription changes
   * (token / id / optedIn). A late-arriving FCM/APNs token — common on slow
   * devices or very short first sessions — fires this even after login() has
   * already run, so we can re-link the authenticated user and emit ONE accurate
   * diagnostic report. Must never throw (it runs on a native callback).
   */
  private handlePushSubscriptionChange(event: any): void {
    try {
      const cur = event?.current || event || {};
      const token = cur?.token || null;
      const id = cur?.id || null;
      const optedIn = cur?.optedIn;
      safeLog.info(
        `[OneSignal:subchange] token=${token ? 'present' : 'missing'} ` +
        `id=${id ? String(id).substring(0, 8) : 'null'} ` +
        `optedIn=${optedIn === true ? 'true' : optedIn === false ? 'false' : 'null'}`
      );

      // Token present is the unambiguous good outcome. Re-link the user (in case
      // the token landed after login) and schedule an UNGATED report — a token
      // can only exist after a grant (or where no prompt is required), so there
      // is no risk of a premature Google first-session log here.
      if (token && this.currentUserId) {
        if (this.lastObservedToken !== token) {
          this.lastObservedToken = token;
          void this.relinkAfterTokenArrival(this.currentUserId);
        }
        this.schedulePushStatusReport('subchange_token', { requirePromptShown: false, delayMs: 800 });
      }
    } catch {
      // observer must never throw
    }
  }

  /**
   * Idempotent re-link used when a push token arrives after login(). Guarded so
   * overlapping observer/verify calls don't fire multiple OneSignal.login()s.
   */
  private async relinkAfterTokenArrival(userId: string): Promise<void> {
    if (this.relinkInFlight) return;
    this.relinkInFlight = true;
    try {
      const OneSignal = this.OneSignalModule;
      if (OneSignal?.login) {
        await OneSignal.login(userId);
        safeLog.info(`[OneSignal:relink] result=success reason=token_arrived userId=${userId.substring(0, 8)}`);
      }
    } catch (e: any) {
      safeLog.warn(`[OneSignal:relink] result=failed reason=token_arrived error=${e?.message || 'Unknown error'}`);
    } finally {
      this.relinkInFlight = false;
    }
  }

  /**
   * Coalesce push-status report triggers into ONE delayed send. The delay lets
   * the APNs/FCM token-acquisition window settle so we report the steady state,
   * not a transient token-missing. An ungated (token-present) request preempts a
   * pending gated one so the definitive outcome wins.
   */
  private schedulePushStatusReport(
    reason: string,
    opts?: { requirePromptShown?: boolean; delayMs?: number }
  ): void {
    if (this.pushStatusReported) return;
    const requirePromptShown = opts?.requirePromptShown ?? false;
    const delay = opts?.delayMs ?? 6000;

    if (this.pushStatusReportTimer) {
      // Let a definitive (ungated) report replace a pending gated one; otherwise
      // don't stack timers.
      if (requirePromptShown === false && this.pendingReportRequirePromptShown !== false) {
        clearTimeout(this.pushStatusReportTimer);
        this.pushStatusReportTimer = null;
      } else {
        return;
      }
    }

    this.pendingReportRequirePromptShown = requirePromptShown;
    safeLog.info(`[OneSignal:diag] scheduling push-status report reason=${reason} gated=${requirePromptShown} inMs=${delay}`);
    this.pushStatusReportTimer = setTimeout(() => {
      this.pushStatusReportTimer = null;
      void this.reportPushStatusOnce({ requirePromptShown: this.pendingReportRequirePromptShown }).catch(() => {});
    }, delay);
  }

  /**
   * RELIABILITY (June 2026): bounded post-login token verification. On Android
   * <13 there is NO permission dialog, so the token is the only thing that can
   * be missing; on slow devices / short sessions it can arrive seconds after
   * login(). This nudges opt-in (ONLY when permission is already granted — never
   * when denied/unknown, which could surface a surprise prompt), polls for the
   * token, re-links idempotently when it lands, and schedules ONE accurate
   * report. Runs at most once per user per app session.
   */
  private async verifyTokenAfterLogin(
    userId: string,
    opts?: { requirePromptShown?: boolean }
  ): Promise<void> {
    if (this.tokenVerifyInProgressFor === userId) return;
    this.tokenVerifyInProgressFor = userId;
    const requirePromptShown = opts?.requirePromptShown ?? true;
    try {
      const OneSignal = this.OneSignalModule;
      const sub = OneSignal?.User?.pushSubscription;
      if (!sub) {
        this.schedulePushStatusReport('verify_no_sub', { requirePromptShown, delayMs: 4000 });
        return;
      }

      // Reliable, non-masked permission read.
      let granted: boolean | null = null;
      try {
        granted = (await OneSignal?.Notifications?.getPermissionAsync?.()) ?? null;
      } catch {
        granted = null;
      }

      if (granted === false) {
        // Explicit denial (iOS / Android 13+) — no token will ever arrive. For a
        // first-session Google signup this is "not asked yet", but the gated
        // report won't send until the prompt-shown flag is set, so it's safe.
        this.schedulePushStatusReport('verify_denied', { requirePromptShown, delayMs: 1000 });
        return;
      }

      // Granted (explicit on 13+/iOS, implicit on Android <13). Nudge opt-in
      // once — safe here because permission is already granted, so it won't
      // prompt — then poll for a (possibly late) token.
      if (granted === true) {
        try { await sub.optIn?.(); } catch { /* non-fatal */ }
      }

      const delays = [0, 1000, 2000, 4000, 8000, 15000];
      for (let i = 0; i < delays.length; i++) {
        if (delays[i]) await new Promise(resolve => setTimeout(resolve, delays[i]));
        let token: string | null = null;
        try { token = (await sub.getTokenAsync?.()) || null; } catch { /* keep polling */ }
        if (token) {
          this.lastObservedToken = token;
          await this.relinkAfterTokenArrival(userId);
          // Token present is definitive — report ungated and promptly.
          this.schedulePushStatusReport('verify_token_present', { requirePromptShown: false, delayMs: 500 });
          return;
        }
      }

      // Token never arrived within the window — the slow-device / short-session
      // failure class. Report it (gated) so we can SEE it in the data.
      safeLog.warn(`[OneSignal:login] token_pending userId=${userId.substring(0, 8)} reason=no_token_after_verify_window`);
      this.schedulePushStatusReport('verify_token_missing', { requirePromptShown, delayMs: 500 });
    } catch {
      // never throw — diagnostics/reliability must not affect login
    } finally {
      this.tokenVerifyInProgressFor = null;
    }
  }

  async getPermissionStatus(): Promise<NotificationPermissionState | null> {
    const envInfo = this.getEnvironmentInfo();
    if (envInfo.isWeb || envInfo.isExpoGo || !envInfo.isSupported) {
      return null;
    }

    try {
      const OneSignal = this.OneSignalModule;

      if (!OneSignal) {
        safeLog.warn('⚠️ OneSignal module not available for permission status check');
        return null;
      }

      let hasNotificationPermission = false;
      let subscribed = false;

      // Check notification permission using v5+ API
      if (OneSignal.Notifications && OneSignal.Notifications.permission) {
        hasNotificationPermission = OneSignal.Notifications.permission;
        safeLog.info('📱 OneSignal notification permission:', hasNotificationPermission);
      }

      // Check subscription status using v5+ API
      if (OneSignal.User && OneSignal.User.pushSubscription) {
        const subscription = OneSignal.User.pushSubscription;
        const subscriptionId = subscription.id || OneSignal.User?.onesignalId;
        subscribed = !!(subscriptionId && subscriptionId !== 'Missing');
      }

      // Fallback: if we can't determine status properly, assume permission was granted
      // if OneSignal initialized successfully
      if (!hasNotificationPermission && this.initialized) {
        safeLog.info('📱 Using fallback: assuming permission granted since OneSignal initialized');
        hasNotificationPermission = true;
      }

      const result = {
        hasNotificationPermission,
        pushDisabled: !hasNotificationPermission,
        subscribed: subscribed && hasNotificationPermission,
      };

      safeLog.info('📱 Final permission status:', result);
      return result;

    } catch (error) {
      safeLog.error('❌ OneSignal permission status check failed:', error?.message || 'Unknown error');

      // Fallback for permission status check errors
      if (this.initialized) {
        safeLog.info('📱 Using error fallback: assuming permission granted due to successful initialization');
        return {
          hasNotificationPermission: true,
          pushDisabled: false,
          subscribed: true,
        };
      }

      return null;
    }
  }

  async ensurePushPermission(userId?: string): Promise<boolean> {
    const envInfo = this.getEnvironmentInfo();
    
    if (envInfo.isWeb || envInfo.isExpoGo || !envInfo.isSupported) {
      safeLog.info('📱 Push notifications not available in this environment');
      return false;
    }

    try {
      const status = await this.getPermissionStatus();
      
      if (status?.subscribed) {
        safeLog.info('✅ User already has push notifications enabled');
        
        // Ensure external user ID is set even if already subscribed
        if (userId) {
          await this.setExternalUserId(userId);
        }
        return true;
      }

      safeLog.info('📱 User not subscribed to push notifications, requesting permission...');
      const granted = await this.requestPermission();
      
      if (granted) {
        safeLog.info('✅ Push notification permission granted');
        
        // Link the device to the user's external ID so OneSignal can target them
        if (userId) {
          safeLog.info('🔗 Linking device to user ID for push notifications...');
          await this.setExternalUserId(userId);
          safeLog.info('✅ Device linked to user ID');
        }
        
        return true;
      } else {
        safeLog.info('❌ Push notification permission denied or not granted');
        return false;
      }
    } catch (error: any) {
      safeLog.error('❌ Error ensuring push permission:', error?.message || 'Unknown error');
      return false;
    }
  }

  /**
   * Get the device push token for server-side registration
   * Returns null if not available (web, Expo Go, or no permission)
   */
  async getPushToken(): Promise<string | null> {
    const envInfo = this.getEnvironmentInfo();

    if (envInfo.isWeb || envInfo.isExpoGo || !envInfo.isSupported) {
      safeLog.info('📱 OneSignal: getPushToken not available in current environment');
      return null;
    }

    try {
      // Ensure initialized
      if (!this.initialized || !this.OneSignalModule) {
        await this.initialize();
      }

      const OneSignal = this.OneSignalModule;
      if (!OneSignal) {
        return null;
      }

      // Get push token from SDK
      const pushToken = await OneSignal?.User?.pushSubscription?.getTokenAsync?.();
      safeLog.info('📱 OneSignal: Push token retrieved:', pushToken ? 'present' : 'not available');
      return pushToken || null;
    } catch (error: any) {
      safeLog.warn('⚠️ OneSignal: Failed to get push token:', error?.message);
      return null;
    }
  }

  /**
   * Get device type for push subscription
   */
  getDeviceType(): 'ios' | 'android' | null {
    if (Platform.OS === 'ios') return 'ios';
    if (Platform.OS === 'android') return 'android';
    return null;
  }

  /**
   * Login user with OneSignal SDK - links external_id (Supabase user ID) to OneSignal user
   * This is the recommended approach per OneSignal best practices
   * 
   * CRITICAL: This function now waits for SDK initialization with retry logic
   */
  /**
   * DIAGNOSTIC (June 2026 push investigation): report the steady-state push
   * status to the SERVER once per app session. Device-side probe logs are
   * invisible for real users, so this lets us see — across real signups —
   * whether new users DENY the prompt vs GRANT it but never obtain a push
   * token (the latter points to native/FCM/dashboard provisioning, not the
   * in-app flow). Runs from setExternalUserId (post-login, so the JWT is
   * present). Sends NO PII: only outcome + platform + app/OS version. Fully
   * fire-and-forget; never affects login or onboarding.
   */
  private async reportPushStatusOnce(opts?: { requirePromptShown?: boolean }): Promise<void> {
    if (this.pushStatusReported || this.pushStatusReportInFlight) return;

    const envInfo = this.getEnvironmentInfo();
    if (envInfo.isWeb || envInfo.isExpoGo || !envInfo.isSupported) return;

    // Login-path trigger: only report once the OS prompt has actually been
    // shown, so first-session Google signups aren't logged before they ever
    // see it. Return WITHOUT claiming the session so a later (post-prompt)
    // call can still report.
    if (opts?.requirePromptShown) {
      try {
        const AsyncStorage = (await import('@react-native-async-storage/async-storage')).default;
        const shown = await AsyncStorage.getItem(this.PUSH_PROMPT_SHOWN_KEY);
        if (shown !== '1') return;
      } catch {
        return; // can't confirm the prompt was shown → don't risk misleading data
      }
    }

    // Re-check after the await above: a concurrent report may have started or
    // completed while we were reading AsyncStorage.
    if (this.pushStatusReported || this.pushStatusReportInFlight) return;
    // RELIABILITY (June 2026): claim only an IN-FLIGHT slot here, NOT "sent".
    // pushStatusReported is set true ONLY after a successful POST below, so a
    // backgrounded app or a failed request leaves the report retryable on the
    // next trigger (login / foreground / token observer).
    this.pushStatusReportInFlight = true;

    try {
      const OneSignal = this.OneSignalModule;
      const sub = OneSignal?.User?.pushSubscription;

      // Reliable, non-masked permission read. getPermissionStatus() has a
      // fallback that reports denied as granted — do NOT use it here.
      let granted: boolean | null = null;
      try {
        granted = (await OneSignal?.Notifications?.getPermissionAsync?.()) ?? null;
      } catch {
        granted = null;
      }

      // APNs/FCM token registration is async after grant — poll briefly so a
      // freshly-granted user isn't mislabelled as "token missing".
      let token: string | null = null;
      let optedIn: boolean | null = null;
      let hasSubscriptionId = false;
      if (sub) {
        const delays = [0, 1500, 2000];
        for (let i = 0; i < delays.length; i++) {
          if (delays[i]) await new Promise(resolve => setTimeout(resolve, delays[i]));
          try {
            token = (await sub.getTokenAsync?.()) || null;
            optedIn = (await sub.getOptedInAsync?.()) ?? null;
            hasSubscriptionId = !!(await sub.getIdAsync?.());
          } catch {
            // keep polling
          }
          if (token) break;
        }
      }

      let outcome:
        | 'granted_token_present'
        | 'granted_token_missing'
        | 'granted_no_subscription_api'
        | 'denied';
      if (granted === false) {
        // Explicit denial dominates: a denied user never gets a subscription/token.
        outcome = 'denied';
      } else if (!sub) {
        outcome = 'granted_no_subscription_api';
      } else if (token) {
        outcome = 'granted_token_present';
      } else {
        outcome = 'granted_token_missing';
      }

      const { apiService } = await import('./apiService');
      const res: any = await apiService.post('/api/onesignal/permission-report', {
        outcome,
        platform: Platform.OS,
        osVersion: String(Platform.Version),
        appVersion: Constants.expoConfig?.version ?? null,
        optedIn,
        hasSubscriptionId,
      });
      // Mark sent ONLY on a confirmed success; otherwise stay retryable so a
      // later trigger (foreground / next launch) can deliver it.
      if (res?.success === true) {
        this.pushStatusReported = true;
        safeLog.info(`[OneSignal:diag] push-status report delivered outcome=${outcome}`);
      } else {
        safeLog.warn(`[OneSignal:diag] push-status report not delivered (will retry) outcome=${outcome}`);
      }
    } catch {
      // Diagnostic only — swallow everything so login is never affected.
      // pushStatusReported stays false → retried on the next trigger.
    } finally {
      this.pushStatusReportInFlight = false;
    }
  }

  async setExternalUserId(userId: string): Promise<boolean> {
    const envInfo = this.getEnvironmentInfo();

    if (envInfo.isWeb) {
      safeLog.info('📱 Web environment - OneSignal login not available');
      this.currentUserId = userId;
      return true;
    }

    if (envInfo.isExpoGo || !envInfo.isSupported) {
      // Greppable diagnostic — a silent skip here means OneSignal.login() is NEVER
      // called, so the external_id is never linked and the user can't receive pushes.
      // Emits every component of isSupported so a device log pinpoints the cause.
      safeLog.warn(
        `[OneSignal:login] result=SKIPPED reason=${envInfo.isExpoGo ? 'expo_go' : 'not_supported'} ` +
        `isSupported=${envInfo.isSupported} isPreviewBuild=${envInfo.isPreviewBuild} ` +
        `hasEnvVars=${envInfo.hasOneSignalEnvVars} disabled=${envInfo.isOneSignalDisabled} ` +
        `platform=${envInfo.platform} execEnv=${envInfo.executionEnvironment} ` +
        `userId=${userId.substring(0, 8)}`
      );
      this.currentUserId = userId;
      return true;
    }

    // Store userId immediately for reference
    this.currentUserId = userId;
    
    // CRITICAL FIX: Ensure OneSignal is initialized before attempting login
    const maxRetries = 3;
    let lastError: any = null;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        // Wait for initialization to complete if not ready
        if (!this.initialized || !this.OneSignalModule) {
          safeLog.info(`🔄 OneSignal: SDK not ready, initializing... (attempt ${attempt}/${maxRetries})`);
          await this.initialize();
          
          // FIX (step 3): Reduced from 1000ms → 100ms — SDK is ready immediately
          // after initialize() completes; the artificial delay was forcing login()
          // to always race behind the SIGNED_IN event (which fires ~300ms post-signup).
          await new Promise(resolve => setTimeout(resolve, 100));
        }
        
        const OneSignal = this.OneSignalModule;
        
        if (!OneSignal) {
          safeLog.info(`⚠️ OneSignal module still not available after init (attempt ${attempt}/${maxRetries})`);
          if (attempt < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, 2000 * attempt));
            continue;
          }
          safeLog.error('❌ OneSignal: Module never became available after all retries');
          return false;
        }

        // Use OneSignal.login() - the SDK recommended way to link external_id
        // This automatically creates/links the user with their external_id
        if (OneSignal?.login) {
          safeLog.info('🔐 OneSignal: Logging in user with external_id:', userId.substring(0, 8) + '...');
          await OneSignal.login(userId);

          // FIX (step 4): Searchable outcome log — easy to grep in production logs.
          safeLog.info(`[OneSignal:login] result=success userId=${userId.substring(0, 8)}`);

          // Set the user's in-app language preference on their OneSignal profile.
          // Without this, OneSignal picks the device OS language, which may differ
          // from the language the user has selected inside the app.
          try {
            const AsyncStorage = (await import('@react-native-async-storage/async-storage')).default;
            const savedLang = await AsyncStorage.getItem('@padrinho:language');
            const lang = (savedLang === 'pt' || savedLang === 'en' || savedLang === 'es')
              ? savedLang
              : 'pt'; // Default to Portuguese — the app's primary user base
            await OneSignal?.User?.setLanguage?.(lang);
            safeLog.info('🌐 OneSignal: User language set to:', lang);
          } catch (langError) {
            safeLog.info('🌐 OneSignal: Could not set language (non-critical)');
          }

          // FIX (step 5): Post-login subscription check — distinguishes two distinct
          // failure modes:
          //   (a) user never created in OneSignal  → fixed by the login() call above
          //   (b) user exists but has no device token → no_device_token (expected if
          //       push permission was never granted; user still exists in OneSignal)
          try {
            const subscriptionId = await OneSignal?.User?.pushSubscription?.getIdAsync?.();
            const optedIn = await OneSignal?.User?.pushSubscription?.getOptedInAsync?.();
            if (!subscriptionId) {
              // User was created in OneSignal but has no push subscription yet.
              // This is EXPECTED when the user has not granted push permission.
              // It is NOT a registration failure — the user exists and can be
              // re-prompted later.
              safeLog.info(`[OneSignal:login] no_device_token userId=${userId.substring(0, 8)} optedIn=${optedIn}`);
            } else {
              safeLog.info(`[OneSignal:login] subscription_active userId=${userId.substring(0, 8)} subscriptionId=${subscriptionId.substring(0, 8)} optedIn=${optedIn}`);
            }
          } catch (subError) {
            safeLog.info('📊 OneSignal: Could not get subscription status (non-critical)');
          }

          // RELIABILITY + DIAGNOSTIC (June 2026): run a bounded post-login token
          // verification. For EMAIL signups the OS prompt fired in onboarding
          // BEFORE this account existed, so the FCM/APNs token may not have
          // arrived by link time (slow device / short session). This nudges
          // opt-in (only when already granted), polls for a late token, re-links
          // idempotently when it lands, and schedules ONE accurate diagnostic
          // report. Gated on the prompt having been shown so a first-session
          // Google signup isn't reported prematurely. The pushSubscription
          // observer is the additional safety net. Fire-and-forget — never
          // delays login.
          void this.verifyTokenAfterLogin(userId, { requirePromptShown: true });

          return true;
        } else if (OneSignal?.User?.addAlias) {
          // Fallback for older SDK versions
          safeLog.info('🔐 OneSignal: Adding external_id alias:', userId.substring(0, 8) + '...');
          await OneSignal.User.addAlias('external_id', userId);
          safeLog.info('✅ OneSignal: External ID alias added successfully');
          return true;
        } else {
          safeLog.info('⚠️ OneSignal login methods not available');
          if (attempt < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, 2000 * attempt));
            continue;
          }
          safeLog.error('❌ OneSignal: Login methods never became available');
          return false;
        }
      } catch (error: any) {
        lastError = error;
        // FIX (step 4): Searchable failure log — easy to grep in production logs.
        safeLog.error(`[OneSignal:login] result=FAILED error=${error?.message || 'Unknown error'} userId=${userId.substring(0, 8)} attempt=${attempt}/${maxRetries}`);
        
        if (attempt < maxRetries) {
          const delay = 2000 * attempt;
          safeLog.info(`⏳ Retrying in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }
    
    safeLog.error(`[OneSignal:login] result=FAILED error=${lastError?.message || 'Unknown error'} userId=${userId.substring(0, 8)} — all ${maxRetries} attempts exhausted`);
    return false;
  }

  getCurrentUserId(): string | null {
    return this.currentUserId;
  }

  async logout(): Promise<boolean> {
    const envInfo = this.getEnvironmentInfo();

    if (envInfo.isWeb) {
      safeLog.info('📱 Web environment - OneSignal logout not available');
      this.currentUserId = null;
      return true;
    }

    if (envInfo.isExpoGo) {
      safeLog.info('📱 Expo Go detected - OneSignal logout skipped');
      this.currentUserId = null;
      return true;
    }

    if (!envInfo.isSupported) {
      safeLog.info('⚠️ OneSignal not supported, logout skipped');
      this.currentUserId = null;
      return true;
    }

    try {
      const OneSignal = this.OneSignalModule;

      if (!OneSignal) {
        safeLog.info('⚠️ OneSignal module not available for logout');
        this.currentUserId = null;
        return true;
      }

      if (OneSignal?.logout) {
        await OneSignal.logout();
        this.currentUserId = null;
        safeLog.info('✅ OneSignal: User logged out successfully');
        return true;
      } else if (OneSignal?.User?.removeExternalUserId) {
        await OneSignal.User.removeExternalUserId();
        this.currentUserId = null;
        safeLog.info('✅ OneSignal: External user ID removed');
        return true;
      } else {
        safeLog.info('⚠️ OneSignal logout methods not available, clearing local state only');
        this.currentUserId = null;
        return true;
      }
    } catch (error: any) {
      safeLog.error('❌ OneSignal logout failed:', error?.message || 'Unknown error');
      this.currentUserId = null;
      return false;
    }
  }

  setNotificationContext(context: any) {
    const envInfo = this.getEnvironmentInfo();

    if (envInfo.isWeb) {
      safeLog.info('📱 Web environment - notification context stored for future use');
      this.notificationContext = context;
      return true;
    }

    if (envInfo.isExpoGo) {
      safeLog.info('📱 Expo Go detected - notification context not set');
      return false;
    }

    if (!this.initialized) {
      safeLog.info('ℹ️ OneSignal: Service not initialized, context stored for later');
      this.notificationContext = context;
      return true;
    }

    this.notificationContext = context;
    safeLog.info('✅ OneSignal: Notification context set');
    return true;
  }

  // Method to check if we're in a web environment
  isWebPlatform(): boolean {
    return this.isWebEnvironment || Platform.OS === 'web';
  }

  // Method to get environment-specific guidance
  getImplementationGuidance(): string {
    const envInfo = this.getEnvironmentInfo();

    if (envInfo.isWeb) {
      return 'For web push notifications, implement OneSignal Web SDK separately from React Native SDK';
    } else if (envInfo.isExpoGo) {
      return 'OneSignal requires EAS build or ejected app - not available in Expo Go';
    } else if (!envInfo.isSupported) {
      return 'OneSignal React Native SDK requires native build environment';
    } else {
      return 'OneSignal React Native SDK properly configured for native environment';
    }
  }

  /**
   * Create notification in database (typically called from webhook)
   */
  static async createDatabaseNotification(data: {
    onesignal_notification_id?: string;
    title: string;
    subtitle?: string;
    message: string;
    banner_url?: string;
    link_url?: string;
    icon?: string;
    background_color?: string;
    target_type?: 'all' | 'user_ids' | 'segments' | 'tags';
    target_user_ids?: string[];
    target_segments?: string[];
    expires_at?: string;
  }): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const notificationId = data.onesignal_notification_id || `webhook-${Date.now()}`;

      // Use centralized save queue for webhook notifications too
      return new Promise((resolve) => {
        OneSignalService.saveQueue.saveNotification(notificationId, async () => {
          try {
            const { NotificationsService } = await import('./notificationsService');
            const result = await NotificationsService.createNotification({
              onesignal_notification_id: data.onesignal_notification_id,
              title: data.title,
              subtitle: data.subtitle,
              message: data.message,
              banner_url: data.banner_url,
              link_url: data.link_url,
              icon: data.icon || 'bell',
              background_color: data.background_color || '#F0EEE4',
              target_type: data.target_type || 'all',
              target_user_ids: data.target_user_ids,
              target_segments: data.target_segments,
              expires_at: data.expires_at,
              is_active: true,
              sent_at: new Date().toISOString()
            });

            if (result.success) {
              resolve(result);
              return true;
            } else {
              resolve(result);
              return false;
            }
          } catch (error) {
            safeLog.error('❌ [OneSignal] Notification callback error:', error);
            const errorResult = { success: false, error: 'Internal server error' };
            resolve(errorResult);
            return false;
          }
        }).then(() => {
          // Promise already resolved in the saveNotification callback
        }).catch((error) => {
          safeLog.error('❌ [OneSignal] Notification promise error:', error);
          resolve({ success: false, error: 'Internal server error' });
        });
      });
    } catch (error) {
      safeLog.error('❌ Error creating database notification:', error);
      return { success: false, error: 'Internal server error' };
    }
  }

  /**
   * Recover failed notification saves from emergency storage
   */
  static async recoverFailedNotificationSaves(): Promise<void> {
    try {
      const AsyncStorage = await import('@react-native-async-storage/async-storage');
      const FAILED_SAVES_KEY = '@padrinho_failed_notification_saves';

      const stored = await AsyncStorage.default.getItem(FAILED_SAVES_KEY);
      if (!stored) {
        safeLog.info('📦 No failed notification saves to recover');
        return;
      }

      const failedSaves = JSON.parse(stored);
      if (!failedSaves.length) {
        safeLog.info('📦 No failed notification saves to recover');
        return;
      }

      safeLog.info(`🔄 Attempting to recover ${failedSaves.length} failed notification saves`);

      let recoveredCount = 0;
      const stillFailed = [];

      for (const failedNotification of failedSaves) {
        try {
          // Remove recovery metadata before saving
          const { failed_at, error, save_attempt_duration_ms, ...cleanNotificationData } = failedNotification;

          // Ensure required fields are present and valid
          if (!cleanNotificationData.title || !cleanNotificationData.message) {
            safeLog.warn('⚠️ Skipping invalid notification data');
            continue;
          }

          const notificationId = cleanNotificationData.onesignal_notification_id || `recovery-${Date.now()}`;

          // Use centralized save queue for recovery too
          const success = await OneSignalService.saveQueue.saveNotification(notificationId, async () => {
            const { NotificationsService } = await import('./notificationsService');
            const result = await NotificationsService.createNotification(cleanNotificationData);
            return result.success;
          });

          if (success) {
            safeLog.info('✅ Recovered failed notification:', cleanNotificationData.title);
            recoveredCount++;
          } else {
            safeLog.warn('⚠️ Still failing to save:', cleanNotificationData.title);
            if (cleanNotificationData.title && cleanNotificationData.message) {
              stillFailed.push(failedNotification);
            }
          }
        } catch (recoveryError) {
          safeLog.warn('⚠️ Recovery attempt failed:', recoveryError.message);
          if (failedNotification.title || failedNotification.onesignal_notification_id) {
            stillFailed.push(failedNotification);
          }
        }

        // Add delay between attempts to avoid overwhelming the database
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      // Update the failed saves storage with any that still failed
      await AsyncStorage.default.setItem(FAILED_SAVES_KEY, JSON.stringify(stillFailed));

      safeLog.info(`🎉 Recovery complete: ${recoveredCount} recovered, ${stillFailed.length} still pending`);

      // Cleanup the save queue periodically
      OneSignalService.saveQueue.cleanup();

    } catch (error) {
      safeLog.error('❌ Failed to recover notification saves:', error);
    }
  }
  
  /**
   * Get comprehensive OneSignal debug status for admin diagnostics
   * Returns all relevant state information for debugging push issues
   */
  async getDebugStatus(options?: { ensureInitialized?: boolean }): Promise<{
    initialized: boolean;
    moduleLoaded: boolean;
    currentUserId: string | null;
    environment: ReturnType<typeof this.getEnvironmentInfo>;
    pushPermission: string | null;
    subscriptionId: string | null;
    optedIn: boolean | null;
    pushToken: string | null;
    onesignalId: string | null;
    lastImportError: string | null;
    lastInitError: string | null;
  }> {
    // Optionally attempt a silent initialize so the panel reflects whether the
    // SDK CAN start, instead of reporting a stale "not initialized" snapshot.
    if (options?.ensureInitialized && !this.initialized) {
      try {
        await this.initialize();
      } catch (e: any) {
        // Failure is captured in this.lastInitError and reported below.
      }
    }

    const envInfo = this.getEnvironmentInfo();
    
    let pushPermission: string | null = null;
    let subscriptionId: string | null = null;
    let optedIn: boolean | null = null;
    let pushToken: string | null = null;
    let onesignalId: string | null = null;
    
    try {
      if (this.initialized && this.OneSignalModule) {
        const OneSignal = this.OneSignalModule;
        
        // Get permission status
        try {
          pushPermission = await OneSignal?.Notifications?.getPermissionAsync?.() ? 'granted' : 'denied';
        } catch (e) {
          pushPermission = 'unknown';
        }
        
        // Get subscription info
        try {
          subscriptionId = await OneSignal?.User?.pushSubscription?.getIdAsync?.() || null;
          optedIn = await OneSignal?.User?.pushSubscription?.getOptedInAsync?.() ?? null;
          pushToken = await OneSignal?.User?.pushSubscription?.getTokenAsync?.() || null;
        } catch (e) {
          safeLog.info('Could not get subscription info:', e);
        }
        
        // Get OneSignal ID
        try {
          onesignalId = await OneSignal?.User?.getOnesignalId?.() || null;
        } catch (e) {
          safeLog.info('Could not get OneSignal ID:', e);
        }
      }
    } catch (error) {
      safeLog.error('Error getting OneSignal debug status:', error);
    }
    
    return {
      initialized: this.initialized,
      moduleLoaded: !!this.OneSignalModule,
      currentUserId: this.currentUserId,
      environment: envInfo,
      pushPermission,
      subscriptionId,
      optedIn,
      pushToken: pushToken ? 'present' : null,
      onesignalId,
      lastImportError: this.lastImportError,
      lastInitError: this.lastInitError
    };
  }

  /**
   * Deep SDK probe for the admin debug panel. Unlike getDebugStatus (which only
   * reports cached singleton state), this ACTIVELY attempts a direct dynamic
   * import of react-native-onesignal and a full initialize(), capturing the
   * EXACT error class/message plus the running native build + OTA update ids.
   * This is the signal that distinguishes a missing native module (needs an EAS
   * rebuild — an OTA can't fix it) from a JS-side init problem (OTA-fixable).
   */
  async probe(): Promise<{
    importOk: boolean;
    importError: string | null;
    importErrorName: string | null;
    moduleKeyCount: number;
    hasInitializeMethod: boolean;
    hasUserMethod: boolean;
    hasNotificationsMethod: boolean;
    initializedAfter: boolean;
    moduleLoadedAfter: boolean;
    initError: string | null;
    subscriptionId: string | null;
    pushToken: string | null;
    optedIn: boolean | null;
    appVersion: string | null;
    nativeAppVersion: string | null;
    nativeBuildVersion: string | null;
    runtimeVersion: string | null;
    updateId: string | null;
    channel: string | null;
    executionEnvironment: string;
    platform: string;
  }> {
    const envInfo = this.getEnvironmentInfo();

    const result = {
      importOk: false,
      importError: null as string | null,
      importErrorName: null as string | null,
      moduleKeyCount: 0,
      hasInitializeMethod: false,
      hasUserMethod: false,
      hasNotificationsMethod: false,
      initializedAfter: false,
      moduleLoadedAfter: false,
      initError: null as string | null,
      subscriptionId: null as string | null,
      pushToken: null as string | null,
      optedIn: null as boolean | null,
      appVersion: (Constants.expoConfig?.version as string) || null,
      nativeAppVersion: null as string | null,
      nativeBuildVersion: null as string | null,
      runtimeVersion: null as string | null,
      updateId: null as string | null,
      channel: null as string | null,
      executionEnvironment: envInfo.executionEnvironment || 'unknown',
      platform: Platform.OS,
    };

    // 1) Direct import attempt — surfaces the exact native-module error.
    try {
      const mod: any = await import('react-native-onesignal');
      result.importOk = true;
      result.moduleKeyCount = Object.keys(mod || {}).length;
      const OS = mod?.OneSignal || mod?.default;
      if (OS) {
        result.hasInitializeMethod = typeof OS.initialize === 'function';
        result.hasUserMethod = !!OS.User;
        result.hasNotificationsMethod = !!OS.Notifications;
      }
    } catch (e: any) {
      result.importOk = false;
      result.importError = e?.message || String(e);
      result.importErrorName = e?.name || null;
    }

    // 2) Full initialize() via the singleton path.
    try {
      await this.initialize();
    } catch (e: any) {
      result.initError = e?.message || String(e);
    }
    result.initializedAfter = this.initialized;
    result.moduleLoadedAfter = !!this.OneSignalModule;
    if (!result.initError && this.lastInitError) result.initError = this.lastInitError;
    if (!result.importError && this.lastImportError) result.importError = this.lastImportError;

    // 3) Subscription details when the module is available.
    if (this.OneSignalModule) {
      try {
        result.subscriptionId =
          (await this.OneSignalModule?.User?.pushSubscription?.getIdAsync?.()) || null;
        result.optedIn =
          (await this.OneSignalModule?.User?.pushSubscription?.getOptedInAsync?.()) ?? null;
        const tok = await this.OneSignalModule?.User?.pushSubscription?.getTokenAsync?.();
        result.pushToken = tok ? 'present' : 'missing';
      } catch {
        /* non-fatal */
      }
    }

    // 4) Build / OTA metadata — which native binary + which update is running.
    try {
      const Application: any = await import('expo-application');
      result.nativeAppVersion = Application?.nativeApplicationVersion ?? null;
      result.nativeBuildVersion = Application?.nativeBuildVersion ?? null;
    } catch {
      /* non-fatal */
    }
    try {
      const Updates: any = await import('expo-updates');
      result.runtimeVersion =
        typeof Updates?.runtimeVersion === 'string' ? Updates.runtimeVersion : null;
      result.updateId = Updates?.updateId ?? null;
      result.channel = Updates?.channel ?? null;
    } catch {
      /* non-fatal */
    }

    safeLog.info('🧪 [OneSignal:probe]', {
      importOk: result.importOk,
      importError: result.importError,
      importErrorName: result.importErrorName,
      initializedAfter: result.initializedAfter,
      moduleLoadedAfter: result.moduleLoadedAfter,
      initError: result.initError,
      channel: result.channel,
      updateId: result.updateId,
      nativeBuildVersion: result.nativeBuildVersion,
    });

    return result;
  }

  /**
   * Force re-register with OneSignal - useful for fixing broken subscriptions
   */
  async forceReRegister(userId: string): Promise<boolean> {
    safeLog.info('🔄 OneSignal: Force re-registering user...');
    
    try {
      // First logout to clear any stale state
      await this.logout();
      
      // Wait for state to clear
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Re-initialize if needed
      if (!this.initialized || !this.OneSignalModule) {
        await this.initialize();
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      
      // Re-login
      const result = await this.setExternalUserId(userId);
      
      if (result) {
        safeLog.info('✅ OneSignal: Force re-registration successful');
      } else {
        safeLog.error('❌ OneSignal: Force re-registration failed');
      }
      
      return result;
    } catch (error: any) {
      safeLog.error('❌ OneSignal: Force re-registration error:', error?.message);
      return false;
    }
  }
}

export default new OneSignalService();